import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Label } from "./ui/label";
import { CheckCircle2, AlertTriangle } from "lucide-react";

export const TEAM_ROLES = [
  { key: "consulting_tech_office", label: "Texniki Ofis" },
  { key: "consulting_inspector", label: "İnspektor" },
  { key: "consulting_topographer", label: "Topoqraf" },
  { key: "consulting_material_engineer", label: "Material Mühəndis" },
  { key: "consulting_estimate_engineer", label: "Smeta Mühəndis" },
  { key: "consulting_team_lead", label: "Team Lead" },
];

/**
 * ConsultingTeamPicker — 6 dropdowns, one per workflow role.
 *
 * Props:
 *  - value: { [role]: user_id }
 *  - onChange: (next) => void
 *  - lockedRoles: array of role keys that should be disabled (user has active tasks)
 *  - testidPrefix
 */
export default function ConsultingTeamPicker({
  value = {},
  onChange,
  lockedRoles = [],
  testidPrefix = "team",
}) {
  const [users, setUsers] = useState([]);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data));
  }, []);

  const usersByRole = (role) => users.filter((u) => u.role === role);
  const complete = TEAM_ROLES.every((r) => value[r.key]);

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-picker`}>
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded-md border text-xs mono uppercase tracking-wide ${
          complete
            ? "border-emerald-200 bg-emerald-50 text-emerald-800"
            : "border-amber-200 bg-amber-50 text-amber-800"
        }`}
        data-testid={`${testidPrefix}-status`}
      >
        {complete ? (
          <>
            <CheckCircle2 className="w-4 h-4" /> Komanda tam — 6/6 rol
          </>
        ) : (
          <>
            <AlertTriangle className="w-4 h-4" />
            {Object.keys(value).filter((k) => value[k]).length}/6 rol təyin edilib
          </>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {TEAM_ROLES.map((r) => {
          const pool = usersByRole(r.key);
          const locked = lockedRoles.includes(r.key);
          return (
            <div key={r.key}>
              <Label className="label-overline flex items-center gap-1">
                {r.label}
                {locked && (
                  <span className="text-[9px] text-red-600 ml-1">
                    (aktiv tapşırıq — kilidli)
                  </span>
                )}
              </Label>
              <Select
                value={value[r.key] || ""}
                onValueChange={(v) =>
                  onChange?.({ ...value, [r.key]: v === "__none" ? null : v })
                }
                disabled={locked}
              >
                <SelectTrigger
                  className="bg-white"
                  data-testid={`${testidPrefix}-${r.key}`}
                >
                  <SelectValue placeholder="Seç..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— Seçilməyib —</SelectItem>
                  {pool.length === 0 && (
                    <div className="px-3 py-2 text-xs text-slate-400">
                      Bu rolda istifadəçi yoxdur
                    </div>
                  )}
                  {pool.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.full_name}
                      {u.organization_name ? ` · ${u.organization_name}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        })}
      </div>
    </div>
  );
}
